import Footer from "./Footer";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface FAQProps {
  changeLanguage: (lang: string) => void;
}

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem = ({ question, answer, isOpen, onClick }: FAQItemProps) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <button
        className="w-full py-6 flex items-center justify-between text-left focus:outline-none group"
        onClick={onClick}
      >
        <span className="text-lg md:text-xl font-gnarly-semibold text-black dark:text-white pr-4">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-6 h-6 text-[#6649ff]" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-gray-700 dark:text-gray-300 font-gnarly-regular text-base md:text-lg leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function FAQ(_props: FAQProps) {
  const { t, i18n } = useTranslation();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({
    "0-0": true,
  });

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const faqCategories = [
    {
      title: t("faq.categories.about_flateroo"),
      items: [
        {
          question: t("faq.items.what_is_flateroo.question"),
          answer: t("faq.items.what_is_flateroo.answer"),
        },
        {
          question: t("faq.items.how_does_flateroo_work.question"),
          answer: t("faq.items.how_does_flateroo_work.answer"),
        },
        {
          question: t("faq.items.is_it_free.question"),
          answer: t("faq.items.is_it_free.answer"),
        },
        {
          question: t("faq.items.availability.question"),
          answer: t("faq.items.availability.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.finding_room"),
      items: [
        {
          question: t("faq.items.how_to_find_wg.question"),
          answer: t("faq.items.how_to_find_wg.answer"),
        },
        {
          question: t("faq.items.how_to_apply.question"),
          answer: t("faq.items.how_to_apply.answer"),
        },
        {
          question: t("faq.items.video_applications.question"),
          answer: t("faq.items.video_applications.answer"),
        },
        {
          question: t("faq.items.good_application.question"),
          answer: t("faq.items.good_application.answer"),
        },
        {
          question: t("faq.items.how_long_to_find.question"),
          answer: t("faq.items.how_long_to_find.answer"),
        },
        {
          question: t("faq.items.what_to_look_for.question"),
          answer: t("faq.items.what_to_look_for.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.listing_flat"),
      items: [
        {
          question: t("faq.items.how_to_list.question"),
          answer: t("faq.items.how_to_list.answer"),
        },
        {
          question: t("faq.items.how_to_find_flatmate.question"),
          answer: t("faq.items.how_to_find_flatmate.answer"),
        },
        {
          question: t("faq.items.what_makes_good_listing.question"),
          answer: t("faq.items.what_makes_good_listing.answer"),
        },
        {
          question: t("faq.items.how_to_screen_flatmates.question"),
          answer: t("faq.items.how_to_screen_flatmates.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.shared_living_tips"),
      items: [
        {
          question: t("faq.items.what_is_wg.question"),
          answer: t("faq.items.what_is_wg.answer"),
        },
        {
          question: t("faq.items.wg_vs_alone.question"),
          answer: t("faq.items.wg_vs_alone.answer"),
        },
        {
          question: t("faq.items.wg_costs.question"),
          answer: t("faq.items.wg_costs.answer"),
        },
        {
          question: t("faq.items.wg_rules.question"),
          answer: t("faq.items.wg_rules.answer"),
        },
        {
          question: t("faq.items.wg_conflicts.question"),
          answer: t("faq.items.wg_conflicts.answer"),
        },
        {
          question: t("faq.items.wg_cleaning.question"),
          answer: t("faq.items.wg_cleaning.answer"),
        },
        {
          question: t("faq.items.wg_utilities.question"),
          answer: t("faq.items.wg_utilities.answer"),
        },
        {
          question: t("faq.items.wg_contract.question"),
          answer: t("faq.items.wg_contract.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.students"),
      items: [
        {
          question: t("faq.items.student_wg.question"),
          answer: t("faq.items.student_wg.answer"),
        },
        {
          question: t("faq.items.student_budget.question"),
          answer: t("faq.items.student_budget.answer"),
        },
        {
          question: t("faq.items.student_first_wg.question"),
          answer: t("faq.items.student_first_wg.answer"),
        },
        {
          question: t("faq.items.international_student.question"),
          answer: t("faq.items.international_student.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.safety_legal"),
      items: [
        {
          question: t("faq.items.safety.question"),
          answer: t("faq.items.safety.answer"),
        },
        {
          question: t("faq.items.subletting_legal.question"),
          answer: t("faq.items.subletting_legal.answer"),
        },
        {
          question: t("faq.items.deposit.question"),
          answer: t("faq.items.deposit.answer"),
        },
        {
          question: t("faq.items.tenant_rights.question"),
          answer: t("faq.items.tenant_rights.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.zurich_wg"),
      items: [
        {
          question: t("faq.items.wg_zurich.question"),
          answer: t("faq.items.wg_zurich.answer"),
        },
        {
          question: t("faq.items.wg_zurich_neighborhoods.question"),
          answer: t("faq.items.wg_zurich_neighborhoods.answer"),
        },
        {
          question: t("faq.items.wg_zurich_prices.question"),
          answer: t("faq.items.wg_zurich_prices.answer"),
        },
        {
          question: t("faq.items.wg_zurich_students.question"),
          answer: t("faq.items.wg_zurich_students.answer"),
        },
        {
          question: t("faq.items.wg_zurich_expats.question"),
          answer: t("faq.items.wg_zurich_expats.answer"),
        },
        {
          question: t("faq.items.wg_zurich_short_term.question"),
          answer: t("faq.items.wg_zurich_short_term.answer"),
        },
        {
          question: t("faq.items.wg_zurich_competition.question"),
          answer: t("faq.items.wg_zurich_competition.answer"),
        },
        {
          question: t("faq.items.wg_zurich_kreis4.question"),
          answer: t("faq.items.wg_zurich_kreis4.answer"),
        },
        {
          question: t("faq.items.wg_zurich_kreis5.question"),
          answer: t("faq.items.wg_zurich_kreis5.answer"),
        },
        {
          question: t("faq.items.wg_zurich_oerlikon.question"),
          answer: t("faq.items.wg_zurich_oerlikon.answer"),
        },
        {
          question: t("faq.items.wg_zurich_altstetten.question"),
          answer: t("faq.items.wg_zurich_altstetten.answer"),
        },
        {
          question: t("faq.items.wg_zurich_seefeld.question"),
          answer: t("faq.items.wg_zurich_seefeld.answer"),
        },
      ],
    },
    {
      title: t("faq.categories.swiss_cities"),
      items: [
        {
          question: t("faq.items.wg_geneva.question"),
          answer: t("faq.items.wg_geneva.answer"),
        },
        {
          question: t("faq.items.wg_geneva_cross_border.question"),
          answer: t("faq.items.wg_geneva_cross_border.answer"),
        },
        {
          question: t("faq.items.wg_basel.question"),
          answer: t("faq.items.wg_basel.answer"),
        },
        {
          question: t("faq.items.wg_bern.question"),
          answer: t("faq.items.wg_bern.answer"),
        },
        {
          question: t("faq.items.wg_lausanne.question"),
          answer: t("faq.items.wg_lausanne.answer"),
        },
        {
          question: t("faq.items.wg_winterthur.question"),
          answer: t("faq.items.wg_winterthur.answer"),
        },
        {
          question: t("faq.items.wg_lucerne.question"),
          answer: t("faq.items.wg_lucerne.answer"),
        },
        {
          question: t("faq.items.wg_stgallen.question"),
          answer: t("faq.items.wg_stgallen.answer"),
        },
        {
          question: t("faq.items.wg_lugano.question"),
          answer: t("faq.items.wg_lugano.answer"),
        },
        {
          question: t("faq.items.wg_fribourg.question"),
          answer: t("faq.items.wg_fribourg.answer"),
        },
      ],
    },
  ];

  // SEO: Meta tags and FAQ Schema structured data
  useEffect(() => {
    // Set page title
    const isGerman = i18n.language === "de";
    document.title = isGerman
      ? "FAQ - WG finden in der Schweiz | Flateroo"
      : "FAQ - Find a WG in Switzerland | Flateroo";

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    const descriptionContent = isGerman
      ? "Häufig gestellte Fragen zu WGs und Wohngemeinschaften in der Schweiz. Tipps für WG-Suche in Zürich, Genf, Basel, Bern, Lausanne und mehr. Alles über Miete, Kosten, Verträge und Mitbewohner finden."
      : "Frequently asked questions about shared flats and WGs in Switzerland. Tips for finding a WG in Zurich, Geneva, Basel, Bern, Lausanne and more. Everything about rent, costs, contracts and finding flatmates.";

    if (metaDescription) {
      metaDescription.setAttribute("content", descriptionContent);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = descriptionContent;
      document.head.appendChild(meta);
    }

    // Meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    const keywordsContent = isGerman
      ? "WG, Wohngemeinschaft, WG Zürich, WG Genf, WG Basel, WG Bern, WG Lausanne, Mitbewohner finden, WG-Zimmer, Zimmer mieten Schweiz, shared flat, flatmate, Studentenwohnung"
      : "WG, Wohngemeinschaft, WG Zurich, WG Geneva, WG Basel, WG Bern, WG Lausanne, find flatmate, WG room, rent room Switzerland, shared flat, flatmate, student housing";

    if (metaKeywords) {
      metaKeywords.setAttribute("content", keywordsContent);
    } else {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      metaKeywords.setAttribute("content", keywordsContent);
      document.head.appendChild(metaKeywords);
    }

    // Open Graph tags for social sharing
    const ogTags = [
      {
        property: "og:title",
        content: isGerman
          ? "FAQ - WG finden in der Schweiz | Flateroo"
          : "FAQ - Find a WG in Switzerland | Flateroo",
      },
      { property: "og:description", content: descriptionContent },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://flateroo.com/faq" },
      { property: "og:site_name", content: "Flateroo" },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute("content", content);
      } else {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        tag.setAttribute("content", content);
        document.head.appendChild(tag);
      }
    });

    // Canonical URL (prevents duplicate content issues)
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", "https://flateroo.com/faq");
    } else {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      canonicalLink.setAttribute("href", "https://flateroo.com/faq");
      document.head.appendChild(canonicalLink);
    }

    // FAQ Schema (JSON-LD) for Google rich snippets
    const allFaqItems = faqCategories.flatMap((category) => category.items);
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    };

    // Remove existing FAQ schema if present
    const existingSchema = document.querySelector(
      'script[type="application/ld+json"][data-faq-schema]',
    );
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new FAQ schema
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-schema", "true");
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const schemaScript = document.querySelector(
        'script[type="application/ld+json"][data-faq-schema]',
      );
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [faqCategories, i18n.language]);

  return (
    <div className="flex flex-col w-full relative bg-[#fffefd] dark:bg-[#1a1a1a] min-h-screen">
      <div className="flex flex-col items-center px-4 md:px-20 pt-12 md:pt-20 pb-12 md:pb-20 flex-grow">
        <div className="w-full max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-gnarly-semibold text-black dark:text-white mb-4 text-center">
            {t("faq.title")}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-12 text-center font-gnarly-regular">
            {t("faq.subtitle")}
          </p>

          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-8">
              <h2 className="text-2xl md:text-3xl font-gnarly-semibold text-[#6649ff] mb-4">
                {category.title}
              </h2>
              <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl shadow-lg p-6 md:p-10">
                {category.items.map((item, itemIndex) => (
                  <FAQItem
                    key={itemIndex}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openItems[`${categoryIndex}-${itemIndex}`] || false}
                    onClick={() => toggleItem(categoryIndex, itemIndex)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default FAQ;
